import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';

import { StringifiedUUID } from 'types';
import { TQPrefetchDataFieldValuesParams } from 'types/dataFieldValue';

export const MAX_PREFETCH_ATTEMPTS = 2;

/**
 * Query params that do not change the record payload the prefetch job caches, and so must be
 * excluded from the token key.
 *
 * The prefetch registry is keyed by a digest of the query params, so producer
 * (usePrefetchDataFieldValues) and consumer (useGetDataFieldsValueQuery) must derive an identical
 * key or the consumer looks up a key that was never registered and its query stays disabled
 * forever — the grid then renders no rows at all.
 *
 * Both entries are approval-mode params that the prefetch params never carry, and neither changes
 * the cached records:
 * - `scopeUserId` only asks the backend to additionally report which rows a *different* collaborator
 *   can see (`scope_user_row_ids`); the requesting user's records are byte-for-byte the same.
 * - `approvalGroupUuid` is consumed after the fetch, in `filter_values_for_filtered_view_set`, to
 *   widen which columns survive a collaborator's column filter. It never reaches the prefetch job.
 *
 * Keying on either would also force a redundant second prefetch, since both only become known once
 * the approval query resolves.
 */
const NON_KEY_QUERY_PARAMS = new Set<string>(['scopeUserId', 'approvalGroupUuid']);

/**
 * Generates a token key for the TQ prefetch token registry.
 * @param params - The prefetch parameters
 * @param shouldNormalizeParams - If true, normalizes empty params to default FPS params (for grid queries).
 *                                If false, keeps empty params as-is
 */
export const generateTokenKey = (params: TQPrefetchDataFieldValuesParams, shouldNormalizeParams = true): string => {
  const { modelId, dataObjectSchemaId, ...queryParams } = params;

  // Sort keys for stringification and filter out undefined/null/empty string values
  const sortedQueryParams = Object.keys(queryParams)
    .filter((key) => !NON_KEY_QUERY_PARAMS.has(key))
    .sort()
    .reduce(
      (acc, key) => {
        const value = queryParams[key as keyof typeof queryParams];
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>,
    );

  // if we have empty params, treat it as default FPS params. This is to fix a race condition.
  // This ensures prefetch with {} generates the same key as query with {"limit":1000,"page":0,"sortAscending":true}
  if (shouldNormalizeParams) {
    const hasOnlyTerritoryParams = Object.keys(sortedQueryParams).every(
      (k) => k === 'territoryId' || k === 'territoryViewId',
    );

    if (Object.keys(sortedQueryParams).length === 0 || hasOnlyTerritoryParams) {
      const normalizedParams: Record<string, any> = {};

      normalizedParams.limit = 1000; // MAX_PAGE_SIZE
      normalizedParams.page = 0;
      normalizedParams.sortAscending = true;

      if (queryParams.territoryId !== undefined) {
        normalizedParams.territoryId = queryParams.territoryId;
      }
      if (queryParams.territoryViewId !== undefined) {
        normalizedParams.territoryViewId = queryParams.territoryViewId;
      }

      return `${modelId}:${dataObjectSchemaId}:${JSON.stringify(normalizedParams)}`;
    }
  }

  return `${modelId}:${dataObjectSchemaId}:${JSON.stringify(sortedQueryParams)}`;
};

type TQPrefetchTokenContextType = {
  getToken: (key: string) => string | undefined;
  getTokenFromParams: (params: TQPrefetchDataFieldValuesParams, shouldNormalizeParams?: boolean) => string | undefined;
  getTokenForSchema: (
    modelId: StringifiedUUID,
    dataObjectSchemaId: StringifiedUUID,
    withParams?: boolean,
  ) => string | undefined;
  registerToken: (key: string, token: string) => void;
  clearToken: (key: string) => void;
  clearAllTokensForModel: (modelId: StringifiedUUID) => void;
  generateTokenKey: typeof generateTokenKey;
  getPrefetchAttempts: (key: string) => number;
  incrementPrefetchAttempts: (key: string) => number;
  resetPrefetchAttempts: (key: string) => void;
  clearAllTokens: () => void;
};

const TQPrefetchTokenContext = createContext<TQPrefetchTokenContextType | undefined>(undefined);

export const useTQPrefetchTokenRegistry = () => {
  const context = useContext(TQPrefetchTokenContext);
  if (!context) {
    throw new Error('useTQPrefetchTokenRegistry must be used within a TQPrefetchTokenProvider');
  }
  return context;
};

type Props = {
  children: ReactNode;
};

export const TQPrefetchTokenProvider = ({ children }: Props) => {
  const [tokens, setTokens] = useState<Record<string, string | undefined>>({});
  // Track the token keys that have been invalidated, used to prevent race conditions
  const invalidatedTokenKeys = useRef<Set<string>>(new Set());
  // Track prefetch attempts per token key for retry limiting
  const [prefetchAttempts, setPrefetchAttempts] = useState<Map<string, number>>(new Map());

  // Clean up invalidation markers
  useEffect(() => {
    const keysToRemove: string[] = [];
    invalidatedTokenKeys.current.forEach((key) => {
      if (!tokens[key]) {
        keysToRemove.push(key);
      }
    });
    keysToRemove.forEach((key) => invalidatedTokenKeys.current.delete(key));
  }, [tokens]);

  const registerToken = useCallback((key: string, token: string) => {
    invalidatedTokenKeys.current.delete(key);
    setTokens((prev) => ({
      ...prev,
      [key]: token,
    }));
  }, []);

  const clearToken = useCallback((key: string) => {
    invalidatedTokenKeys.current.add(key);
    setTokens((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  }, []);

  const resetPrefetchAttempts = useCallback((tokenKey: string) => {
    setPrefetchAttempts((prev) => {
      const newMap = new Map(prev);
      newMap.delete(tokenKey);
      return newMap;
    });
  }, []);

  const clearAllTokensForModel = useCallback(
    (modelId: StringifiedUUID) => {
      const keysToInvalidate = Object.keys(tokens).filter((key) => key.startsWith(`${modelId}:`));
      keysToInvalidate.forEach((key) => {
        invalidatedTokenKeys.current.add(key);
        resetPrefetchAttempts(key);
      });

      setTokens((prev) => {
        const updated = { ...prev };
        keysToInvalidate.forEach((key) => {
          delete updated[key];
        });
        return updated;
      });
    },
    [tokens, resetPrefetchAttempts],
  );

  const getToken = useCallback(
    (key: string): string | undefined => {
      if (invalidatedTokenKeys.current.has(key)) {
        return undefined;
      }
      return tokens[key];
    },
    [tokens],
  );

  const getTokenFromParams = useCallback(
    (params: TQPrefetchDataFieldValuesParams, shouldNormalizeParams = true): string | undefined => {
      const key = generateTokenKey(params, shouldNormalizeParams);
      return getToken(key);
    },
    [getToken],
  );

  const getTokenForSchema = useCallback(
    (modelId: StringifiedUUID, dataObjectSchemaId: StringifiedUUID, withParams = false): string | undefined => {
      const prefix = `${modelId}:${dataObjectSchemaId}:`;
      const allKeys = Object.keys(tokens);
      const matchingKey = allKeys.find(
        (key) =>
          key.startsWith(prefix) &&
          tokens[key] &&
          !invalidatedTokenKeys.current.has(key) &&
          // The params are tokenized as stringified JSON, so if we're explicitly asking for the params
          // we need to exclude the 'empty' params case, i.e. {}
          (!withParams || key.slice(prefix.length) !== '{}'),
      );
      return matchingKey ? tokens[matchingKey] : undefined;
    },
    [tokens],
  );

  const getPrefetchAttempts = useCallback(
    (tokenKey: string): number => {
      return prefetchAttempts.get(tokenKey) || 0;
    },
    [prefetchAttempts],
  );

  const incrementPrefetchAttempts = useCallback(
    (tokenKey: string): number => {
      const currentAttempts = prefetchAttempts.get(tokenKey) || 0;
      const newAttempts = currentAttempts + 1;
      setPrefetchAttempts((prev) => new Map(prev).set(tokenKey, newAttempts));
      return newAttempts;
    },
    [prefetchAttempts],
  );

  const clearAllTokens = useCallback(() => {
    setTokens({});
    setPrefetchAttempts(new Map());
  }, []);

  const contextValue = useMemo(
    () => ({
      getToken,
      getTokenFromParams,
      getTokenForSchema,
      registerToken,
      clearToken,
      clearAllTokensForModel,
      generateTokenKey,
      getPrefetchAttempts,
      incrementPrefetchAttempts,
      resetPrefetchAttempts,
      clearAllTokens,
    }),
    [
      getToken,
      getTokenFromParams,
      getTokenForSchema,
      registerToken,
      clearToken,
      clearAllTokensForModel,
      getPrefetchAttempts,
      incrementPrefetchAttempts,
      resetPrefetchAttempts,
      clearAllTokens,
    ],
  );

  return <TQPrefetchTokenContext.Provider value={contextValue}>{children}</TQPrefetchTokenContext.Provider>;
};
